import React, { useContext, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { searchOrganization } from "../../api/endpoints/search";
import { copyCourse } from "../../api/endpoints/training";
import { callOptionsHeaders } from "../../api/helpers";
import { regionValuesArr } from "../../api/index";
import { DocumentSearch } from "../../components/search/constants";
import { SnackbarContext } from "../../context/snackbar-context";
import {
  convertUUIDtoS12Id,
  validateS12ID,
  validateUUID,
} from "../../helpers/s12-helpers";
import { CopyCoursePresenter } from "./copy-course-presenter";
import { LocationState, RegionSearchState, SearchState } from "./types";

export const CopyCourseContainer: React.FC = () => {
  const location = useLocation<LocationState>();
  const { sourceOrgId, sourceRegionCode, course } = location.state || {};
  const { showError } = useContext(SnackbarContext);
  const history = useHistory();

  const [searchState, setSearchState] = useState<SearchState>({
    orgName: "",
    roleId: "",
    errorMessage: "",
    hasSearched: false,
    overwriteExisting: false,
  });

  const [regionSearchStates, setRegionSearchStates] = useState<
    Record<string, RegionSearchState>
  >(
    regionValuesArr.reduce(
      (acc, region) => ({
        ...acc,
        [region]: {
          isLoading: false,
          roles: [],
          copyStatus: {},
        },
      }),
      {}
    )
  );

  const handleSearch = () => {
    const { orgName, roleId } = searchState;

    if (!(orgName || roleId)) {
      setSearchState((prev) => ({
        ...prev,
        errorMessage: "Please enter either an organization name or role ID",
        hasSearched: false,
      }));
      return;
    }

    if (
      roleId &&
      !(
        validateUUID(roleId) ||
        (validateS12ID(roleId) && roleId.startsWith(DocumentSearch.Role))
      )
    ) {
      setSearchState((prev) => ({
        ...prev,
        errorMessage: "Invalid Role ID format",
      }));
      return;
    }

    setSearchState((prev) => ({
      ...prev,
      errorMessage: "",
      hasSearched: true,
    }));

    const searchPromises = regionValuesArr.map((region) => {
      setRegionSearchStates((prev) => ({
        ...prev,
        [region]: { ...prev[region], isLoading: true, copyStatus: {} },
      }));

      return searchOrganization(
        {
          pageSize: 100,
          offset: 0,
          orgName,
          roleId: roleId && convertUUIDtoS12Id(roleId, DocumentSearch.Role),
        },
        callOptionsHeaders(region)
      )
        .then((response) => {
          setRegionSearchStates((prev) => ({
            ...prev,
            [region]: {
              isLoading: false,
              roles: response.roles,
              copyStatus: {},
            },
          }));
        })
        .catch((error) => {
          showError(error);
          setRegionSearchStates((prev) => ({
            ...prev,
            [region]: { ...prev[region], isLoading: false },
          }));
        });
    });

    return Promise.all(searchPromises);
  };

  const handleCopyCourse = (targetOrgId: string, targetRegionCode: string) => {
    setRegionSearchStates((prev) => ({
      ...prev,
      [targetRegionCode]: {
        ...prev[targetRegionCode],
        copyStatus: {
          ...prev[targetRegionCode].copyStatus,
          [targetOrgId]: { isLoading: true, isSuccess: false },
        },
      },
    }));

    return copyCourse(
      {
        fromOrganisationID: sourceOrgId,
        courseID: course.id,
        toOrganisationID: targetOrgId,
        toOrganisationRegion: targetRegionCode,
        overwrite: searchState.overwriteExisting,
      },
      callOptionsHeaders(sourceRegionCode, sourceOrgId)
    )
      .then(() => {
        setRegionSearchStates((prev) => ({
          ...prev,
          [targetRegionCode]: {
            ...prev[targetRegionCode],
            copyStatus: {
              ...prev[targetRegionCode].copyStatus,
              [targetOrgId]: { isLoading: false, isSuccess: true },
            },
          },
        }));
      })
      .catch((error) => {
        showError(error);
        setRegionSearchStates((prev) => ({
          ...prev,
          [targetRegionCode]: {
            ...prev[targetRegionCode],
            copyStatus: {
              ...prev[targetRegionCode].copyStatus,
              [targetOrgId]: { isLoading: false, isSuccess: false },
            },
          },
        }));
      });
  };

  if (!sourceOrgId || !sourceRegionCode || !course) {
    history.replace("/");
    return null;
  }

  return (
    <CopyCoursePresenter
      course={course}
      searchState={searchState}
      regionSearchStates={regionSearchStates}
      sourceOrgId={sourceOrgId}
      sourceRegionCode={sourceRegionCode}
      onSearch={handleSearch}
      onCopyCourse={handleCopyCourse}
      onBack={() => history.goBack()}
      onSearchStateChange={(changes: Partial<SearchState>) =>
        setSearchState((prev) => ({ ...prev, ...changes }))
      }
    />
  );
};
